import type { ReactNode } from 'react';
import { cn } from '@/shared/lib/cn';

type PageLayoutProps = {
  /** The page's `h1`, set in Sectra. */
  title: ReactNode;
  description?: ReactNode;
  /** Buttons on the right of the title. */
  actions?: ReactNode;
  /** `default` ≈ 1024px column; `narrow` ≈ 672px for forms and reading. */
  width?: 'default' | 'narrow';
  children: ReactNode;
  className?: string;
};

/**
 * The "Page" template: a centred, roomy, max-width column at the default density (16px, cards).
 * Goes inside the app shell's `main`. For full-bleed tooling use `WorkspaceLayout`.
 */
function PageLayout({
  title,
  description,
  actions,
  width = 'default',
  children,
  className,
}: PageLayoutProps) {
  return (
    <div
      data-slot="page-layout"
      data-testid="page-layout"
      data-width={width}
      className={cn(
        'mx-auto w-full space-y-8 px-4 py-8 sm:px-8',
        width === 'narrow' ? 'max-w-2xl' : 'max-w-5xl',
        className,
      )}
    >
      <header className="flex flex-wrap items-start justify-between gap-x-6 gap-y-3">
        <div className="min-w-0 space-y-1">
          <h1 className="font-display break-words">{title}</h1>
          {description ? <p className="max-w-prose text-muted-foreground">{description}</p> : null}
        </div>
        {actions ? (
          <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>
        ) : null}
      </header>
      {children}
    </div>
  );
}

export { PageLayout, type PageLayoutProps };
